/**
 * Bridge layer between SimOps studies and the MeshGen workspace.
 *
 * These types describe the per-DP user-meshing lifecycle that wraps the
 * existing MeshGen panels. The backend `StudyStatus` will gain an
 * `awaiting_user_meshing` value (planned); the UI here is shaped so the
 * components can be wired up once that lands without prop changes.
 */

/** Per-design-point meshing state, used by the progress list and DP switcher. */
export type DpMeshingStatus =
  | 'not_started'
  | 'meshing'
  | 'mesh_ready'
  | 'faces_done'
  | 'failed';

export interface MeshingDpRow {
  id: string;
  /** Short DP slug rendered in mono — e.g. `dp-02`. */
  dp: string;
  /** Geometry / variant name. */
  geom: string;
  /** Variant detail line (e.g. `60 m/s · 110 kPa`). */
  variant: string;
  status: DpMeshingStatus;
  /** Cell count text once the mesh is produced. */
  cells?: string;
  /** Face naming progress, e.g. `5 of 6 named`. */
  facesPending?: string;
  facesCount?: string;
  /** Overrides the default status text. Used for failure detail. */
  statusNote?: string;
}

export interface DpTabItem {
  id: string;
  dp: string;
  /** Short variant label shown next to the DP slug in the tab. */
  short: string;
  status: DpMeshingStatus;
}

/**
 * Steps in the SimOps study lifecycle. There is no Setup step — input
 * picking lives inside the Simulate view, so drafts land there directly
 * and the journey is the three phases that do real work.
 */
export const JOURNEY_STEPS = [
  { id: 'mesh', label: 'Mesh', sub: 'Health + settings' },
  { id: 'simulate', label: 'Simulate', sub: 'Parameters + run' },
  { id: 'results', label: 'Results', sub: 'Analysis' },
] as const;

export type JourneyStepId = (typeof JOURNEY_STEPS)[number]['id'];
